// Field modifier system for real-time field updates in ContentEditor
const fieldModifiers = {}

/**
 * Add a field modifier for a specific content type
 * @param {string} contentType - The content type name
 * @param {string} fieldId - The field that triggers the modification
 * @param {function} modifier - Function that takes (fieldValue, allFieldValues) and returns updated field values
 */
export const addFieldModifier = (contentType, fieldId, modifier) => {
  if (!fieldModifiers[contentType]) {
    fieldModifiers[contentType] = {}
  }
  fieldModifiers[contentType][fieldId] = modifier
}

/**
 * Apply field modifiers when a field changes
 * @param {string} contentType - The content type name  
 * @param {string} changedFieldId - The field that was changed
 * @param {*} newValue - The new value of the changed field
 * @param {object} currentValues - All current field values
 * @returns {object} ONLY the changed field plus whatever the modifier
 *                    returned — a delta, never a copy of every field. The
 *                    caller merges it into the LATEST state; returning a
 *                    full copy of `currentValues` (a render-time closure)
 *                    re-applied stale values over newer ones, which is how
 *                    an edit could be silently reverted.
 */
export const applyFieldModifiers = (contentType, changedFieldId, newValue, currentValues) => {
  // The delta only — the caller merges it into the latest state.
  const delta = { [changedFieldId]: newValue }

  // Check if there are modifiers for this content type and field
  const typeModifiers = fieldModifiers[contentType]
  if (!typeModifiers || !typeModifiers[changedFieldId]) return delta

  // Apply the modifier — it still sees the complete set of values.
  const modifier = typeModifiers[changedFieldId]
  const modifications = modifier(newValue, { ...currentValues, ...delta })

  // Merge the modifications
  return { ...delta, ...modifications }
}

/**
 * Check if a field has modifiers
 * @param {string} contentType - The content type name
 * @param {string} fieldId - The field ID to check
 * @returns {boolean}
 */
export const hasFieldModifier = (contentType, fieldId) => {
  return !!(fieldModifiers[contentType] && fieldModifiers[contentType][fieldId])
}

/**
 * Apply all field modifiers for a content type on initial load
 * This runs all modifiers to ensure computed fields are properly set
 * @param {string} contentType - The content type name
 * @param {object} initialValues - The initial field values
 * @returns {object} Updated field values with all modifiers applied
 */
export const applyAllFieldModifiers = (contentType, initialValues) => {
  const typeModifiers = fieldModifiers[contentType]
  if (!typeModifiers) return initialValues
  
  let updatedValues = { ...initialValues }
  
  // Apply each field modifier
  Object.entries(typeModifiers).forEach(([fieldId, modifier]) => {
    const fieldValue = updatedValues[fieldId]
    if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
      const modifications = modifier(fieldValue, updatedValues)
      updatedValues = { ...updatedValues, ...modifications }
    }
  })
  
  return updatedValues
}